import { useCallback, useEffect, useRef, useState } from "react"
import { Pressable, TouchableOpacity, View } from "react-native"
import {
  BottomSheetModal,
  BottomSheetBackdrop,
  type BottomSheetBackdropProps,
} from "@gorhom/bottom-sheet"
import { type FeatureOptionProps } from "@/constants/BuyBox"
import ScrollPicker from "@/components/scroll/ScrollPicker"
import ChevronDown from "@/assets/icons/chevron-down.svg"
import ChevronUp from "@/assets/icons/chevron-up.svg"
import { BFColors } from "@/constants/Colors"
import BFText, { H5 } from "../standard/BFText"

interface FeatureRangeProps {
  title: string
  minValue: string | number | null
  maxValue: string | number | null
  minOptions: FeatureOptionProps[]
  maxOptions: FeatureOptionProps[]
  onValuesChanged: (
    minVal: string | number | null,
    maxVal: string | number | null
  ) => void
}

const FeatureRange: React.FC<FeatureRangeProps> = ({
  title,
  minValue,
  maxValue,
  minOptions,
  maxOptions,
  onValuesChanged,
}: FeatureRangeProps) => {
  const bottomSheetModalRef = useRef<BottomSheetModal>(null)
  const [expanded, setExpanded] = useState<boolean>(false)
  const [min, setMin] = useState<string | number | null>(minValue)
  const [max, setMax] = useState<string | number | null>(maxValue)

  useEffect(() => {
    setMin(minValue)
    setMax(maxValue)
  }, [minValue, maxValue])

  const onExpand = useCallback(() => {
    setExpanded(true)
    bottomSheetModalRef.current?.present()
  }, [])

  const onCollapse = useCallback(() => {
    setExpanded(false)
    bottomSheetModalRef.current?.dismiss()
  }, [])

  const onDone = useCallback(() => {
    let saveMax = max
    if (
      min &&
      typeof min === "number" &&
      max &&
      typeof max === "number" &&
      max < min
    ) {
      saveMax = min
    }

    onValuesChanged(min, saveMax)
    onCollapse()
  }, [onValuesChanged, onCollapse, min, max])

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        appearsOnIndex={0}
        disappearsOnIndex={-1}
      />
    ),
    []
  )

  let displayValue = "Any"
  if (minValue !== null || maxValue !== null) {
    const minName = minOptions.find((opt) => opt.value === minValue)?.name
    const maxName = maxOptions.find((opt) => opt.value === maxValue)?.name
    displayValue = `${minName} - ${maxName}`
  }

  return (
    <>
      <Pressable
        testID={"expand-button"}
        className="flex flex-row justify-between h-[62px] items-center"
        onPress={onExpand}
      >
        <H5 className="text-midnight-blue">{title}</H5>
        <View className="flex flex-row">
          <H5 className="text-calm-blue mr-2">{displayValue}</H5>
          {expanded ? (
            <ChevronUp width={21} height={21} />
          ) : (
            <ChevronDown width={21} height={21} />
          )}
        </View>
      </Pressable>
      <BottomSheetModal
        ref={bottomSheetModalRef}
        snapPoints={["40%"]}
        backdropComponent={renderBackdrop}
        index={0}
        backgroundStyle={{ backgroundColor: BFColors.calmBlue }}
        handleIndicatorStyle={{ backgroundColor: "#FFFFFF" }}
        onDismiss={onCollapse}
        enableContentPanningGesture={false}
        enableDynamicSizing={false}
      >
        <View className="flex flex-col flex-1">
          <View className="flex items-end">
            <TouchableOpacity testID={"done-button"} onPress={onDone}>
              <BFText className="text-[14px] font-spacegrotesk text-white mr-6">
                Done
              </BFText>
            </TouchableOpacity>
          </View>
          <BFText className="text-white font-figtree-bold text-[18px] text-center m-8">
            {title}
          </BFText>
          <View className="flex flex-col h-[180px]">
            <View className="flex flex-row flex-1 items-center">
              <ScrollPicker
                options={minOptions}
                value={minValue}
                onValueChanged={setMin}
              />
              <BFText className="text-white font-spacegrotesk">to</BFText>
              <ScrollPicker
                options={maxOptions}
                value={maxValue}
                onValueChanged={setMax}
              />
            </View>
          </View>
        </View>
      </BottomSheetModal>
    </>
  )
}

export default FeatureRange
